import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  FiActivity,
  FiClock,
  FiEdit3,
  FiMessageSquare,
  FiPlus,
  FiRefreshCcw,
  FiSave,
  FiTrash2,
} from "react-icons/fi";
import Navbar from "../navbar/navbar";
import "./mcp-blog.css";
import config from "../../config/config.json";

const API_BASE = config.blogApiURL;

const categories = [
  { value: "tech", label: "Tech" },
  { value: "finance", label: "Finance" },
  { value: "lifestyle", label: "Lifestyle" },
];

const emptyPostForm = {
  title: "",
  author: "",
  category: "",
  body: "",
};

const emptyCommentForm = {
  commenter: "",
  text: "",
};

const AUTHOR_MAX_LENGTH = 40;

function formatDate(dateStr) {
  if (!dateStr) return "";

  try {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function trimPreview(text, max = 130) {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}...`;
}

function MCPBlogPage({
  currentPage,
  isAIChatOpen,
  openAIChat,
  theme,
  toggleTheme,
}) {
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [activePostId, setActivePostId] = useState("");
  const [postForm, setPostForm] = useState(emptyPostForm);
  const [commentForm, setCommentForm] = useState(emptyCommentForm);
  const [postMessage, setPostMessage] = useState({ type: "", text: "" });
  const [commentError, setCommentError] = useState("");
  const [isPostsLoading, setIsPostsLoading] = useState(true);
  const [isPostSaving, setIsPostSaving] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isCommentSaving, setIsCommentSaving] = useState(false);

  const activePost = useMemo(
    () => posts.find((post) => post._id === activePostId) || null,
    [posts, activePostId]
  );

  const postCount = posts.length;
  const commentCount = comments.length;
  const lastUpdated = activePost?.updatedAt || activePost?.createdAt || "";

  const showPostMessage = (type = "", text = "") => {
    setPostMessage({ type, text });
  };

  const apiRequest = async (path, options = {}) => {
    const response = await fetch(path, {
      headers: { "Content-Type": "application/json" },
      ...options,
    });

    const contentType = response.headers.get("content-type");
    const isJson = contentType && contentType.includes("application/json");
    const body = isJson ? await response.json() : null;

    if (!response.ok) {
      throw new Error(body?.error || `Request failed (${response.status})`);
    }

    return body;
  };

  const resetEditor = () => {
    setActivePostId("");
    setPostForm(emptyPostForm);
    setComments([]);
    setCommentForm(emptyCommentForm);
    setCommentError("");
    showPostMessage();
  };

  const loadPosts = async (preferredId = "") => {
    setIsPostsLoading(true);
    showPostMessage();

    try {
      const items = await apiRequest(API_BASE);
      setPosts(items);

      const nextId =
        preferredId && items.some((post) => post._id === preferredId)
          ? preferredId
          : activePostId && items.some((post) => post._id === activePostId)
            ? activePostId
            : "";

      if (!items.length) {
        resetEditor();
      } else if (nextId) {
        setActivePostId(nextId);
      }
    } catch (error) {
      showPostMessage("error", error.message || "Failed to load posts.");
    } finally {
      setIsPostsLoading(false);
    }
  };

  const loadComments = async (postId) => {
    if (!postId) {
      setComments([]);
      return;
    }

    setIsCommentsLoading(true);
    setCommentError("");

    try {
      const items = await apiRequest(`${API_BASE}/${postId}/comments`);
      setComments(items);
    } catch (error) {
      setCommentError(error.message || "Failed to load comments.");
      setComments([]);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const loadPostDetail = async (postId) => {
    if (!postId) {
      resetEditor();
      return;
    }

    showPostMessage();
    setCommentError("");

    try {
      const post = await apiRequest(`${API_BASE}/${postId}`);
      setActivePostId(post._id);
      setPostForm({
        title: post.title || "",
        author: post.author || "",
        category: post.category || "",
        body: post.body || "",
      });
      await loadComments(post._id);
    } catch (error) {
      showPostMessage("error", error.message || "Failed to load post.");
    }
  };

  useEffect(() => {
    loadPosts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activePostId) return;
    const exists = posts.some((post) => post._id === activePostId);
    if (!exists) {
      resetEditor();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, activePostId]);

  const handlePostFieldChange = (field, value) => {
    setPostForm((current) => ({ ...current, [field]: value }));
  };

  const handleCommentFieldChange = (field, value) => {
    setCommentForm((current) => ({ ...current, [field]: value }));
  };

  const validatePostForm = () => {
    if (!postForm.title.trim() || !postForm.author.trim() || !postForm.category || !postForm.body.trim()) {
      return "Please fill in all fields.";
    }
    if (postForm.title.trim().length < 5) return "Title must be at least 5 characters.";
    if (postForm.author.trim().length < 3) return "Author must be at least 3 characters.";
    if (postForm.author.trim().length > AUTHOR_MAX_LENGTH) {
      return `Author must be ${AUTHOR_MAX_LENGTH} characters or less.`;
    }
    if (postForm.body.trim().length < 50) return "Body must be at least 50 characters.";
    return "";
  };

  const handlePostSubmit = async (event) => {
    event.preventDefault();

    const validationError = validatePostForm();
    if (validationError) {
      showPostMessage("error", validationError);
      return;
    }

    setIsPostSaving(true);
    showPostMessage();

    const payload = {
      title: postForm.title.trim(),
      author: postForm.author.trim(),
      category: postForm.category,
      body: postForm.body.trim(),
    };

    try {
      const isEditing = Boolean(activePostId);
      const savedPost = await apiRequest(
        isEditing ? `${API_BASE}/${activePostId}` : API_BASE,
        {
          method: isEditing ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );

      await loadPosts(savedPost._id);
      await loadPostDetail(savedPost._id);
      showPostMessage("success", isEditing ? "Post updated." : "Post created.");
    } catch (error) {
      showPostMessage("error", error.message || "Failed to save post.");
    } finally {
      setIsPostSaving(false);
    }
  };

  const handleDeletePost = async () => {
    if (!activePostId) return;

    const confirmed = window.confirm("Delete this post and all its comments?");
    if (!confirmed) return;

    setIsDeletingPost(true);
    showPostMessage();

    try {
      await apiRequest(`${API_BASE}/${activePostId}`, { method: "DELETE" });
      showPostMessage("success", "Post deleted.");
      resetEditor();
      await loadPosts();
    } catch (error) {
      showPostMessage("error", error.message || "Failed to delete post.");
    } finally {
      setIsDeletingPost(false);
    }
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();

    if (!activePostId) {
      setCommentError("Select a post first.");
      return;
    }

    if (!commentForm.commenter.trim() || !commentForm.text.trim()) {
      setCommentError("Please fill in your name and comment.");
      return;
    }

    if (commentForm.text.trim().length < 10) {
      setCommentError("Comment must be at least 10 characters.");
      return;
    }

    setIsCommentSaving(true);
    setCommentError("");

    try {
      await apiRequest(`${API_BASE}/${activePostId}/comments`, {
        method: "POST",
        body: JSON.stringify({
          commenter: commentForm.commenter.trim(),
          text: commentForm.text.trim(),
        }),
      });
      setCommentForm(emptyCommentForm);
      await loadComments(activePostId);
    } catch (error) {
      setCommentError(error.message || "Failed to add comment.");
    } finally {
      setIsCommentSaving(false);
    }
  };

  return (
    <>
      <Navbar
        currentPage={currentPage}
        theme={theme}
        toggleTheme={toggleTheme}
        isAIChatOpen={isAIChatOpen}
        openAIChat={openAIChat}
      />

      <div className="mcp-page">
        <div className="mcp-shell">
          <motion.section
            className="mcp-hero"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="mcp-hero-copy">
              <span className="mcp-eyebrow">Model Context Protocol</span>
              <h1>MCP Blog Workspace</h1>
            </div>

            <div className="mcp-hero-stats">
              <div className="mcp-stat-card">
                <FiEdit3 />
                <div>
                  <strong>{postCount}</strong>
                  <span>Posts</span>
                </div>
              </div>
              <div className="mcp-stat-card">
                <FiMessageSquare />
                <div>
                  <strong>{activePostId ? commentCount : 0}</strong>
                  <span>Comments</span>
                </div>
              </div>
              <div className="mcp-stat-card">
                <FiClock />
                <div>
                  <strong>{lastUpdated ? formatDate(lastUpdated) : "Waiting"}</strong>
                  <span>Last update</span>
                </div>
              </div>
            </div>
          </motion.section>

          <div className="mcp-workspace">
            <motion.aside
              className="mcp-panel mcp-posts-panel"
              initial={{ opacity: 0, x: -18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.05 }}
            >
              <div className="mcp-panel-head">
                <div>
                  <span className="mcp-panel-kicker">Live feed</span>
                  <h2>Posts</h2>
                </div>
                <button
                  type="button"
                  className="mcp-btn mcp-btn-ghost"
                  onClick={() => loadPosts(activePostId)}
                  disabled={isPostsLoading}
                >
                  <FiRefreshCcw />
                  Refresh
                </button>
              </div>

              <div className="mcp-scroll-area mcp-posts-list">
                {isPostsLoading ? (
                  <div className="mcp-empty-state">
                    <p>Loading posts...</p>
                  </div>
                ) : posts.length ? (
                  posts.map((post) => (
                    <button
                      type="button"
                      key={post._id}
                      className={`mcp-post-card ${activePostId === post._id ? "is-active" : ""}`}
                      onClick={() => loadPostDetail(post._id)}
                      title={post.title}
                    >
                      <div className="mcp-post-card-main">
                        <h3 title={post.title}>{post.title}</h3>
                        <p title={post.body}>{trimPreview(post.body)}</p>
                      </div>
                      <div className="mcp-post-card-meta">
                        <span className="mcp-pill mcp-pill-category" title={post.category}>
                          {post.category}
                        </span>
                        <span className="mcp-pill" title={post.author}>
                          {post.author}
                        </span>
                        <span className="mcp-pill" title={formatDate(post.createdAt)}>
                          {formatDate(post.createdAt)}
                        </span>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="mcp-empty-state">
                    <p>No posts yet. Create your first post on the right.</p>
                  </div>
                )}
              </div>
            </motion.aside>

            <motion.section
              className="mcp-panel mcp-editor-panel"
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.1 }}
            >
              <div className="mcp-panel-head">
                <div>
                  <span className="mcp-panel-kicker">
                    {activePostId ? "Editing post" : "Create a post"}
                  </span>
                </div>

                <div className="mcp-toolbar">
                  {activePostId ? (
                    <>
                      <button
                        type="button"
                        className="mcp-btn mcp-btn-ghost"
                        onClick={resetEditor}
                      >
                        <FiPlus />
                        New
                      </button>
                      <button
                        type="button"
                        className="mcp-btn mcp-btn-danger"
                        onClick={handleDeletePost}
                        disabled={isDeletingPost}
                      >
                        <FiTrash2 />
                        Delete
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="mcp-scroll-area mcp-editor-scroll">
                <form className="mcp-form" onSubmit={handlePostSubmit}>
                  <div className="mcp-form-grid mcp-form-grid-top">
                    <label className="mcp-field">
                      <span>Title</span>
                      <input
                        type="text"
                        value={postForm.title}
                        onChange={(event) => handlePostFieldChange("title", event.target.value)}
                        placeholder="e.g. Building APIs with MCP"
                        required
                      />
                      <small>At least 5 characters.</small>
                    </label>

                    <label className="mcp-field">
                      <span>Author</span>
                      <input
                        type="text"
                        value={postForm.author}
                        onChange={(event) => handlePostFieldChange("author", event.target.value)}
                        placeholder="Your name"
                        maxLength={AUTHOR_MAX_LENGTH}
                        required
                      />
                      <small>3 to {AUTHOR_MAX_LENGTH} characters.</small>
                    </label>

                    <label className="mcp-field">
                      <span>Category</span>
                      <select
                        value={postForm.category}
                        onChange={(event) => handlePostFieldChange("category", event.target.value)}
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <label className="mcp-field">
                    <span>Body</span>
                    <textarea
                      rows="5"
                      value={postForm.body}
                      onChange={(event) => handlePostFieldChange("body", event.target.value)}
                      placeholder="Write something interesting about your topic..."
                      required
                    />
                    <small>At least 50 characters.</small>
                  </label>

                  {postMessage.text ? (
                    <div className={`mcp-alert ${postMessage.type === "error" ? "is-error" : "is-success"}`}>
                      {postMessage.text}
                    </div>
                  ) : null}

                  <div className="mcp-form-actions">
                    <button
                      type="submit"
                      className="mcp-btn mcp-btn-primary"
                      disabled={isPostSaving}
                    >
                      {isPostSaving ? <FiActivity /> : <FiSave />}
                      {isPostSaving
                        ? activePostId ? "Saving..." : "Creating..."
                        : activePostId ? "Save Changes" : "Create Post"}
                    </button>
                  </div>
                </form>

                <section className="mcp-comments-block">
                  <div className="mcp-comments-head">
                    <div>
                      <h3>Comments</h3>
                      <p>
                        {activePost
                          ? `Comments for "${activePost.title}"`
                          : "Select a post to view comments."}
                      </p>
                    </div>
                    <span className="mcp-comments-count">
                      {activePost ? `${commentCount} total` : "No post selected"}
                    </span>
                  </div>

                  <div className="mcp-comments-list">
                    {isCommentsLoading ? (
                      <div className="mcp-empty-state">
                        <p>Loading comments...</p>
                      </div>
                    ) : activePost ? (
                      comments.length ? (
                        comments.map((comment, index) => (
                          <div className="mcp-comment-card" key={`${comment.createdAt}-${index}`}>
                            <p>{comment.text}</p>
                            <div className="mcp-comment-meta">
                              <span className="mcp-pill">{comment.commenter}</span>
                              <span className="mcp-pill">{formatDate(comment.createdAt)}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="mcp-empty-state">
                          <p>No comments yet. Be the first to comment.</p>
                        </div>
                      )
                    ) : (
                      <div className="mcp-empty-state">
                        <p>No post selected.</p>
                      </div>
                    )}
                  </div>

                  <form className="mcp-comment-form" onSubmit={handleCommentSubmit}>
                    <label className="mcp-field mcp-field-compact">
                      <span>Name</span>
                      <input
                        type="text"
                        value={commentForm.commenter}
                        onChange={(event) => handleCommentFieldChange("commenter", event.target.value)}
                        placeholder="Your name"
                        required
                      />
                    </label>

                    <label className="mcp-field mcp-field-compact">
                      <span>Comment</span>
                      <input
                        type="text"
                        value={commentForm.text}
                        onChange={(event) => handleCommentFieldChange("text", event.target.value)}
                        placeholder="Add a comment (min 10 chars)"
                        required
                      />
                    </label>

                    <button
                      type="submit"
                      className="mcp-btn mcp-btn-secondary"
                      disabled={!activePostId || isCommentSaving}
                    >
                      <FiMessageSquare />
                      {isCommentSaving ? "Sending..." : "Add Comment"}
                    </button>
                  </form>

                  {commentError ? (
                    <div className="mcp-alert is-error is-compact">{commentError}</div>
                  ) : null}
                </section>
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </>
  );
}

export default MCPBlogPage;
